<template>
  <div v-if="show" class="modal-overlay" @click.self="handleClose">
    <div class="modal-container">
      <div class="modal-header">
        <h3>Edição em Lote</h3>
        <button @click="handleClose" class="close-btn">&times;</button>
      </div>
      
      <div class="modal-body">
        <div class="info-section">
          <p><strong>{{ selectedItems.length }}</strong> itens selecionados</p>
          <div class="selected-items-preview" v-if="selectedItems.length <= 5">
            <ul>
              <li v-for="item in selectedItems" :key="item.id">
                {{ item.equipamento_nome || item.equipamento || `Item ID: ${item.id}` }}
              </li>
            </ul>
          </div>
          <div v-else class="selected-items-preview">
            <p>{{ getItemsPreviewText() }} e mais {{ selectedItems.length - 3 }} itens...</p>
          </div>
        </div>

        <div class="edit-options">
          <h4>Tipo de Alteração</h4>
          <div class="radio-group">
            <label>
              <input type="radio" v-model="changeType" value="percentage" />
              Alteração Percentual
            </label>
            <label>
              <input type="radio" v-model="changeType" value="fixed" />
              Valor Fixo
            </label>
          </div>

          <div v-if="changeType === 'percentage'" class="percentage-section">
            <h5>Alteração Percentual</h5>
            <div class="percentage-inputs">
              <div class="input-group">
                <label>Percentagem:</label>
                <input 
                  type="number" 
                  v-model.number="percentageChange" 
                  step="0.01"
                  placeholder="Ex: 10 para +10%, -5 para -5%"
                  class="standard-input"
                  :class="{ 'error-input': percentageChange !== null && percentageChange < -100 }"
                  min="-100"
                />
                <span class="percentage-symbol">%</span>
              </div>
              
              <div v-if="percentageValidationMessage" class="validation-error">
                <i class="fa-solid fa-exclamation-triangle"></i>
                {{ percentageValidationMessage }}
              </div>
              
              <div class="fields-to-change">
                <h6>Aplicar alteração ao Preço de Venda:</h6>
                <div class="price-info">
                  <p><i class="fa-solid fa-info-circle"></i> Esta alteração será aplicada ao campo 'venda' de todos os itens selecionados</p>
                  <p><strong>Exemplo:</strong> -20% aplica desconto de 20%, +15% aumenta preço em 15%</p>
                  <p><strong>Limite:</strong> Desconto máximo é -100% (itens gratuitos) para evitar preços negativos</p>
                </div>
              </div>
            </div>
          </div>

          <div v-if="changeType === 'fixed'" class="fixed-value-section">
            <h5>Valores Fixos</h5>
            <div class="fixed-inputs">
              <div class="input-group">
                <label>Novo Preço de Venda ({{ currentCurrencySymbol }}):</label>
                <input 
                  type="number" 
                  v-model.number="fixedValues.venda" 
                  step="0.01"
                  placeholder="Insira o novo preço fixo"
                  class="standard-input"
                  :class="{ 'error-input': fixedValues.venda !== null && fixedValues.venda < 0 }"
                  min="0"
                />
              </div>
              
              <div v-if="fixedValueValidationMessage" class="validation-error">
                <i class="fa-solid fa-exclamation-triangle"></i>
                {{ fixedValueValidationMessage }}
              </div>
              
              <div class="fixed-info">
                <p><i class="fa-solid fa-info-circle"></i> Todos os itens selecionados terão o preço de venda alterado para este valor exato</p>
                <p><strong>Permitido:</strong> Use 0 para itens gratuitos, valores negativos não são permitidos</p>
              </div>
            </div>
          </div>

          <div v-if="isBaseTable" class="apply-to-all-section">
            <h5>Aplicar a Outras Tabelas</h5>
            <div class="checkbox-group">
              <label class="checkbox-container">
                <input 
                  type="checkbox" 
                  v-model="applyToOtherTables" 
                  class="apply-checkbox"
                />
                <span class="checkmark"></span>
                Aplicar estas alterações a todas as outras tabelas de preço
              </label>
              <div class="apply-info">
                <p><i class="fa-solid fa-info-circle"></i> Esta opção aplicará as mesmas alterações a todos os itens correspondentes em outras tabelas de preço</p>
              </div>
            </div>
          </div>

          <div class="preview-section" v-if="hasValidChanges">
            <h5>Pré-visualização das Alterações</h5>
            <div class="preview-table">
              <table>
                <thead>
                  <tr>
                    <th>Artigo</th>
                    <th>Preço Atual</th>
                    <th v-if="changeType === 'percentage'">
                      Novo Preço ({{ getPreviewText() }})
                    </th>
                    <th v-if="changeType === 'fixed'">
                      Novo Preço (Fixo)
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in previewItems" :key="item.id">
                    <td>{{ item.artigo_codigo || item.equipamento_nome }}</td>
                    <td>{{ formatCurrency(item.venda) }}</td>
                    <td v-if="changeType === 'percentage'">
                      {{ formatCurrency(calculateNewPrice(item.venda)) }}
                    </td>
                    <td v-if="changeType === 'fixed'">
                      {{ formatCurrency(fixedValues.venda) }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      
      <div class="modal-footer">
        <button @click="handleClose" class="btn btn-secondary">Cancelar</button>
        <button 
          @click="applyChanges" 
          :disabled="!hasValidChanges"
          class="btn btn-primary"
        >
          Aplicar Alterações ({{ selectedItems.length }} itens)
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ItemPrecarioBulkEditPopUp',
  props: {
    show: {
      type: Boolean,
      default: false
    },
    isBaseTable: {
      type: Boolean,
      default: false
    },
    selectedItems: {
      type: Array,
      default: () => []
    },
    currentCurrency: {
      type: String,
      default: 'EUR'
    },
    currentCurrencySymbol: {
      type: String,
      default: '€'
    },
    currentFilters: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      changeType: 'percentage', // 'percentage' or 'fixed'
      percentageChange: null,
      fieldsToChange: {
        venda: true
      },
      fixedValues: {
        venda: null
      },
      applyToOtherTables: false
    };
  },
  computed: {
    hasValidChanges() {
      if (this.changeType === 'percentage') {
        return this.percentageChange !== null && 
               this.percentageChange !== 0 &&
               this.percentageChange >= -100; // Allow -100% (free items) but prevent negative prices
      } else {
        return this.fixedValues.venda !== null && 
               this.fixedValues.venda !== undefined && 
               this.fixedValues.venda >= 0; // Allow 0 (free items) but prevent negative prices
      }
    },
    
    percentageValidationMessage() {
      if (this.percentageChange !== null && this.percentageChange < -100) {
        return 'Desconto não pode ser maior que 100% (resultaria em preços negativos)';
      }
      return null;
    },
    
    fixedValueValidationMessage() {
      if (this.fixedValues.venda !== null && this.fixedValues.venda < 0) {
        return 'Preço não pode ser negativo. Use 0 para itens gratuitos';
      }
      return null;
    },
    
    previewItems() {
      // Show first 5 items for preview
      return this.selectedItems.slice(0, 5);
    }
  },
  methods: {
    handleClose() {
      this.resetForm();
      this.$emit('close');
    },
    
    resetForm() {
      this.changeType = 'percentage';
      this.percentageChange = null;
      this.fieldsToChange = {
        venda: true
      };
      this.fixedValues = {
        venda: null
      };
      this.applyToOtherTables = false;
    },
    
    applyChanges() {
      if (!this.hasValidChanges) {
        return;
      }
      
      // Additional validation for percentage
      if (this.changeType === 'percentage' && this.percentageChange < -100) {
        alert('Erro: Desconto não pode ser maior que 100% pois resultaria em preços negativos.');
        return;
      }
      
      // Additional validation for fixed values
      if (this.changeType === 'fixed' && this.fixedValues.venda < 0) {
        alert('Erro: Preço fixo não pode ser negativo. Use 0 para itens gratuitos.');
        return;
      }
      
      const changes = {
        type: this.changeType,
        applyToOtherTables: this.applyToOtherTables
      };
      
      if (this.changeType === 'percentage') {
        changes.percentage = this.percentageChange;
        changes.fields = this.fieldsToChange; // Keep original structure
      } else {
        changes.values = this.fixedValues; // Keep original structure
      }
      
      this.$emit('apply-changes', changes);
    },
    
    calculateNewPrice(currentPrice) {
      if (this.changeType === 'percentage') {
        const multiplier = 1 + (this.percentageChange / 100);
        return currentPrice * multiplier;
      }
      return currentPrice;
    },
    
    getPreviewText() {
      if (this.percentageChange > 0) {
        return `+${this.percentageChange}%`;
      } else {
        return `${this.percentageChange}%`;
      }
    },
    
    getItemsPreviewText() {
      return this.selectedItems.slice(0, 3).map(item => 
        item.equipamento_nome || item.equipamento || `Item ID: ${item.id}`
      ).join(', ');
    },
    
    formatCurrency(value) {
      if (value === null || value === undefined) {
        return '-';
      }
      return new Intl.NumberFormat('pt-PT', {
        style: 'currency',
        currency: this.currentCurrency
      }).format(value);
    }
  },
  watch: {
    show(newVal) {
      if (!newVal) {
        this.resetForm();
      }
    }
  }
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.modal-container {
  background: var(--fundoApp);
  border-radius: 10px;
  max-width: 800px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  /* box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3); */
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  background-color: var(--fundoForm);
  border-radius: 10px 10px 0 0;
}

.modal-header h3 {
  margin: 0;
  color: var(--titulo);
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: var(--icon);
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-btn:hover {
  color: var(--titulo);
}

.modal-body {
  padding: 1.5rem;
  background: var(--fundoForm);
  color: var(--text);
}

.info-section {
  background-color: rgba(93, 93, 152, 0.55);
  padding: 1rem;
  border-radius: 10px;
  margin-bottom: 1.5rem;
  color: var(--label);
}

.selected-items-preview ul {
  margin: 0.5rem 0 0 0;
  padding-left: 1rem;
}

.selected-items-preview li {
  font-size: 0.9rem;
  color: var(--text);
}

.edit-options h4, .edit-options h5, .edit-options h6 {
  margin: 1rem 0 0.5rem 0;
  color: var(--titulo);
}

.radio-group {
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
}

.radio-group label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  color: var(--label);
}

.percentage-section, .fixed-value-section {
  background-color: rgba(93, 93, 152, 0.55);
  padding: 1rem;
  border-radius: 10px;
  margin-top: 1rem;
}

.input-group {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.input-group label {
  min-width: 120px;
  font-weight: 500;
  color: var(--label);
}

.input-group input[type="number"] {
  padding: 0.5rem;
  border: 1px solid #cccccc;
  border-radius: 5px;
  width: 270px;
  background-color: var(--label);
  color: var(--text);
}

.percentage-symbol {
  font-weight: bold;
  color: var(--text);
}

.fields-to-change {
  margin-top: 1rem;
}

.fields-to-change label {
  display: block;
  margin-bottom: 0.5rem;
  cursor: pointer;
  color: var(--label);
}

.fields-to-change input[type="checkbox"] {
  margin-right: 0.5rem;
  accent-color: var(--button);
}

.radio-group input[type="radio"] {
  margin-right: 0.5rem;
  accent-color: var(--button);
  /* color: red; */
}

.fixed-inputs .input-group {
  flex-direction: column;
  align-items: flex-start;
}

.fixed-inputs .input-group input {
  width: 270px;
  background-color: var(--label);
  color: var();
  border: 1px solid #cccccc;
  border-radius: 5px;
}

.preview-section {
  margin-top: 1.5rem;
  padding: 1rem;
  background-color: rgba(93, 93, 152, 0.55);
  border-radius: 10px;
}

.preview-table {
  overflow-x: auto;
  margin-top: 1rem;
}

.preview-table table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
  background-color: rgba(0, 0, 0, 0.1);
  border-radius: 5px;
  overflow: hidden;
}

.preview-table th,
.preview-table td {
  padding: 0.5rem;
  text-align: left;
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: var(--text);
}

.preview-table th {
  background-color: rgba(93, 93, 152, 0.8);
  font-weight: 600;
  color: var(--label);
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  padding: 1rem 1.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  background-color: var(--fundoForm);
  border-radius: 0 0 10px 10px;
}

.btn {
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  font-weight: 500;
  transition: background-color 0.2s;
}

.btn-secondary {
  background-color: #626288;
  color: var(--label);
}

.btn-secondary:hover {
  background-color: #545b62;
}

.btn-primary {
  background-color: var(--button);
  color: var(--label);
}

.btn-primary:hover:not(:disabled) {
  background-color: #5d5d98;
}

.btn:disabled {
  background-color: #7B7B99;
  cursor: not-allowed;
  color: rgba(255, 255, 255, 0.5);
}

/* Info sections */
.price-info, .fixed-info {
  margin-top: 1rem;
  padding: 0.75rem;
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: 6px;
  border-left: 4px solid var(--button);
}

.price-info p, .fixed-info p {
  margin: 0.25rem 0;
  font-size: 0.9rem;
  color: var(--text);
}

.price-info i, .fixed-info i {
  color: var(--button);
  margin-right: 0.5rem;
}

.standard-input {
  font-size: 14px !important;
  font-weight: 500;
  text-align: left;
}

.standard-input:focus {
  border-color: var(--button) !important;
  box-shadow: 0 0 0 2px rgba(93, 93, 152, 0.3) !important;
}

/* Validation error styles */
.error-input {
  border-color: #dc3545 !important;
  background-color: rgba(220, 53, 69, 0.1) !important;
}

.validation-error {
  margin-top: 0.5rem;
  padding: 0.5rem;
  background-color: rgba(220, 53, 69, 0.1);
  border: 1px solid #dc3545;
  border-radius: 4px;
  color: #dc3545;
  font-size: 0.85rem;
}

.validation-error i {
  margin-right: 0.5rem;
}

/* Apply to other tables section */
.apply-to-all-section {
  background-color: rgba(93, 93, 152, 0.55);
  padding: 1rem;
  border-radius: 10px;
  margin-top: 1rem;
}

.checkbox-group {
  margin-top: 0.5rem;
}

.checkbox-container {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
  color: var(--label);
  font-weight: 500;
  margin-bottom: 0.5rem;
}

.apply-checkbox {
  width: 18px;
  height: 18px;
  accent-color: var(--button);
  cursor: pointer;
}


.apply-info {
  margin-top: 0.75rem;
  padding: 0.75rem;
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: 6px;
  border-left: 4px solid var(--button);
}

.apply-info p {
  margin: 0;
  font-size: 0.9rem;
  color: var(--text);
}

.apply-info i {
  color: var(--button);
  margin-right: 0.5rem;
}
</style>
